import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EditableInfoRow } from "@/components/shared/EditableInfoRow";
import { ProjectCustomerCard } from "./ProjectCustomerCard";
import { InvoicePlanCard } from "./InvoicePlanCard";
import type { DBInvoice, DBProject } from "@/lib/db-types";
import type { CustomerInfo } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";

interface InnstillingerTabProps {
  project: DBProject;
  customer: CustomerInfo | null;
  allUsers: UserLookup[];
  /** VIB-2836: faktureringsplanen låser ledd som allerede er fakturert. */
  invoices: DBInvoice[];
  /** VIB-722c: gate fakturadetaljer-redigering. */
  canEditInvoiceTerms?: boolean;
  /** VIB-722c: gate redigering av total_amount / kontraktssum. */
  canEditContractAmount?: boolean;
}

export function InnstillingerTab({
  project: p,
  customer,
  allUsers,
  invoices,
  canEditInvoiceTerms = true,
  canEditContractAmount = true,
}: InnstillingerTabProps) {
  const userOptions = (allUsers ?? []).map((u) => ({ value: u.id, label: u.full_name }));
  // Faktura-felter rendres bare som visning når brukeren ikke har edit_invoice_terms.
  // canEditContractAmount (PL + kunderådgiver + admin) gater prosjekttype-feltet
  // under Prosjektinfo — samme økonomiske vekt som kontraktssum (VIB-1148).

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Roller */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Roller</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <EditableInfoRow
              label="Kunderådgiver"
              value={p.advisor_id}
              field="advisor_id"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="select"
              options={userOptions}
            />
            <EditableInfoRow
              label="Prosjektleder"
              value={p.production_lead_id}
              field="production_lead_id"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="select"
              options={userOptions}
            />
          </CardContent>
        </Card>

        {/* Fakturering */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fakturering</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <EditableInfoRow
              label="Betalingsfrist"
              value={p.payment_due_days != null ? String(p.payment_due_days) : null}
              field="payment_due_days"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="number"
              suffix=" dager"
              readOnly={!canEditInvoiceTerms}
            />
            <EditableInfoRow
              label="Fakturamail"
              value={p.invoice_email}
              field="invoice_email"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="text"
              readOnly={!canEditInvoiceTerms}
            />
            <EditableInfoRow
              label="Deres ref."
              value={p.deres_ref}
              field="deres_ref"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="text"
              readOnly={!canEditInvoiceTerms}
            />
            <EditableInfoRow
              label="Deres ref. kode"
              value={p.deres_ref_kode}
              field="deres_ref_kode"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="text"
              readOnly={!canEditInvoiceTerms}
            />
            {/* VIB-909: PL-styrt flagg — reise faktureres per medgått tid.
                Boolean rendres som select (true/false), samme mønster som
                is_internal under Prosjektinfo. */}
            <EditableInfoRow
              label="Reise faktureres separat (medgått)"
              value={p.bills_travel_per_medgatt ? "true" : "false"}
              field="bills_travel_per_medgatt"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="select"
              options={[
                { value: "true", label: "Ja" },
                { value: "false", label: "Nei" },
              ]}
              readOnly={!canEditInvoiceTerms}
            />
          </CardContent>
        </Card>

        {/* VIB-2836: faktureringsplanen erstatter det frittstående oppstartsprosent-feltet
            (startup_invoice_pct skrives sammen med planen = første ledd). Kun fastpris med
            ekstern fakturering har en plan. */}
        {p.project_type !== "hourly" && !p.is_internal && (
          <InvoicePlanCard project={p} invoices={invoices} canEdit={canEditInvoiceTerms} />
        )}

        {/* Prosjektinfo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Prosjektinfo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {/* VIB-1206: rediger prosjektnavn. NOT NULL → required. Gated til
                PL/rådgiver/admin (samme nivå som kontraktssum). Navneendring
                logges av trg_log_project_name_change (project_renamed). */}
            <EditableInfoRow
              label="Prosjektnavn"
              value={p.name}
              field="name"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="text"
              required
              readOnly={!canEditContractAmount}
            />
            {/* VIB-1148: bytt mellom fastpris og T&M (løpende timer). Gated til
                PL og oppover (edit_contract_amount). NOT NULL → allowEmpty=false. */}
            <EditableInfoRow
              label="Prosjekttype"
              value={p.project_type}
              field="project_type"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="select"
              options={[
                { value: "fixed", label: "Fastpris" },
                { value: "hourly", label: "T&M (løpende timer)" },
              ]}
              allowEmpty={false}
              readOnly={!canEditContractAmount}
            />
            <EditableInfoRow
              label="Est. slutt faktura"
              value={p.estimated_deadline}
              field="estimated_deadline"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="date"
            />
            {/* VIB-1297: valgfri forventet oppstartsdato — samme redigeringsmønster som estimated_deadline. */}
            <EditableInfoRow
              label="Estimert oppstart"
              value={p.estimated_start_date}
              field="estimated_start_date"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="date"
            />
            <EditableInfoRow
              label="Harddisk"
              value={p.harddisk}
              field="harddisk"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="text"
            />
            <EditableInfoRow
              label="Internt prosjekt"
              value={p.is_internal ? "Ja" : "Nei"}
              field="is_internal"
              projectId={p.id}
              projectUpdatedAt={p.updated_at}
              type="select"
              options={[
                { value: "true", label: "Ja" },
                { value: "false", label: "Nei" },
              ]}
            />
            {/* Project code er server-autoritativt for eksterne prosjekter
                (PO-sync). For interne (INT##-serien) tillater vi manuell
                redigering — DB UNIQUE-constraint blokkerer kollisjoner. */}
            {p.is_internal && (
              <EditableInfoRow
                label="Prosjektkode"
                value={p.project_code}
                field="project_code"
                projectId={p.id}
                projectUpdatedAt={p.updated_at}
                type="text"
              />
            )}
          </CardContent>
        </Card>

        <ProjectCustomerCard project={p} customer={customer} />
      </div>
    </div>
  );
}
